<template>
  <BreadCrumb class="hidden sm:block" />
  <ExerciseNav :pages="entry?.children || []" />
  <ExercisesPage v-if="entry" :entry="entry" />
</template>

<script>
import gql from 'graphql-tag';
import { stripLocale } from '@/utils/i18n';
import { isFiltersQuery, enrichBlocksWithExerciseData } from '@/utils/algolia';
import { SeomaticFragment, EntryPageFragment } from '@/mixins/entryPage';
import ExercisesPage from '@/components/exercises/ExercisesPage';
import { ExercisePageFragment } from '@/graphql/cms/ExercisePageFragment';
import ExerciseNav from '@/components/exercises/ExerciseNav';
import { useEntryPage } from '@/composables/useEntryPage';
import BreadCrumb from '@/components/layout/BreadCrumb';

export const ExerciseSetupQuery = gql`
  query ExerciseSetupQuery($site: String!, $slug: String!) {
    entry(level: 1, section: "exercises", site: [$site], slug: [$slug]) {
      title
      ... on exercisesPage_Entry {
        children(level: 2, orderBy: "lft") {
          slug
          uri
          title
        }
      }
    }
  }
`;

export const ExercisesPageQuery = gql`
  query ExercisesPageQuery($site: String!, $uri: String!) {
    entry(section: "exercises", site: [$site], uri: [$uri]) {
      title
      ...EntryPageFragment
      ...ExercisePageFragment
    }

    seomatic(site: $site, uri: $uri, asArray: true) {
      ...SeomaticFragment
    }
  }
  ${EntryPageFragment}
  ${ExercisePageFragment}
  ${SeomaticFragment}
`;

export default defineNuxtComponent({
  name: 'Exercises',

  components: {
    ExercisesPage,
    ExerciseNav,
    BreadCrumb,
  },

  async asyncData() {
    const { $craftRequest } = useNuxtApp();
    const route = useRoute();
    const { locale } = useNuxtApp().$i18n;

    // Get the URI path for content fetching
    const uri = stripLocale(route.path, locale.value);

    // Get the slug for navigation setup
    // Extract the first part of the path for the ExerciseNav component
    const slug = uri.split('/')[0] || '';

    // First, fetch the navigation data
    const navVariables = { site: locale.value, slug };
    const { entry: navEntry } = await $craftRequest(ExerciseSetupQuery, navVariables);

    // Then fetch the content data
    const contentVariables = { site: locale.value, uri };
    const { entry, seomatic } = await $craftRequest(ExercisesPageQuery, contentVariables);

    // Enrich blocks with algolia exercise data
    await enrichBlocksWithExerciseData(entry.contentBlocks);

    // Combine the navigation and content data
    entry.children = navEntry.children;

    return { entry, seomatic };
  },

  created() {
    useEntryPage(this.entry, this.seomatic, this.gtmMeta);
  },

  computed: {
    gtmMeta() {
      const options = { content_category: 'exercises' };
      if (isFiltersQuery(this.$route.query)) options.page_type = this.entry?.pageProfile;
      return options;
    },
  },
});
</script>
